import { ArrowUpRight, ArrowDownRight, ArrowRight, ChevronRight } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sparkline } from './Sparkline'

export interface StatTrend {
  direction: 'up' | 'down' | 'stable'
  label?: string
}

interface QuickStatCardProps {
  icon: LucideIcon
  /** e.g. 'bg-error-500/10 text-error-500' — icon chip tint + glyph color */
  iconClassName?: string
  title: string
  value: string
  subtitle?: string
  trend?: StatTrend
  sparklineData?: number[]
  sparklineColorClass?: string
  onClick?: () => void
  className?: string
}

const trendStyles = {
  up: { icon: ArrowUpRight, className: 'bg-success-600/10 text-success-600' },
  down: { icon: ArrowDownRight, className: 'bg-error-500/10 text-error-500' },
  stable: { icon: ArrowRight, className: 'bg-gray-400/10 text-gray-400' },
} as const

/**
 * Compact stat card: icon chip + label header, big rounded-numeral value,
 * optional trend pill / subtitle / sparkline. Tappable when onClick given.
 */
export function QuickStatCard({
  icon: Icon,
  iconClassName = 'bg-primary-600/10 text-primary-600',
  title,
  value,
  subtitle,
  trend,
  sparklineData,
  sparklineColorClass,
  onClick,
  className,
}: QuickStatCardProps) {
  const TrendIcon = trend ? trendStyles[trend.direction].icon : null

  const content = (
    <>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg',
            iconClassName
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-[13px] font-medium text-gray-500">{title}</span>
        {onClick && <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />}
      </div>
      <div className="flex items-center gap-2">
        <span className="font-rounded text-2xl font-semibold tabular-nums text-gray-900">
          {value}
        </span>
        {trend && TrendIcon && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full text-[11px] font-medium',
              trend.label ? 'px-1.5 py-0.5' : 'h-5 w-5 justify-center',
              trendStyles[trend.direction].className
            )}
          >
            <TrendIcon className="h-3 w-3" />
            {trend.label}
          </span>
        )}
      </div>
      {subtitle && <span className="text-[11px] text-gray-400">{subtitle}</span>}
      {sparklineData && sparklineData.length > 1 && (
        <Sparkline data={sparklineData} colorClass={sparklineColorClass} />
      )}
    </>
  )

  const cardClass = cn('hearth-card flex flex-col gap-2 p-4 text-left', className)

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(cardClass, 'hearth-press')}>
        {content}
      </button>
    )
  }
  return <div className={cardClass}>{content}</div>
}
